/**
 * Change Password page component.
 * Allows users to change their password with validation.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  LockReset as LockResetIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuthStore } from '@/stores/authStore';

// Password validation matching backend requirements
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .refine((password) => {
    // Count character types (lowercase, uppercase, digit, special)
    let typeCount = 0;
    if (/[a-z]/.test(password)) typeCount++;
    if (/[A-Z]/.test(password)) typeCount++;
    if (/\d/.test(password)) typeCount++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) typeCount++;
    return typeCount >= 3;
  }, 'Password must contain at least 3 of: lowercase, uppercase, digit, special character');

// Validation schema
const changePasswordSchema = z
  .object({
    old_password: z.string().min(1, 'Current password is required'),
    new_password: passwordSchema,
    confirm_password: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export default function ChangePassword() {
  const navigate = useNavigate();
  const { changePassword, isLoading, error, clearError, mustChangePassword, user } =
    useAuthStore();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      old_password: '',
      new_password: '',
      confirm_password: '',
    },
  });

  // Clear error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const onSubmit = async (data: ChangePasswordFormData) => {
    setSuccessMessage(null);

    try {
      await changePassword({
        old_password: data.old_password,
        new_password: data.new_password,
      });

      setSuccessMessage('Password changed successfully! Redirecting to login...');

      // Redirect to login page after success (session is now invalid)
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err) {
      // Error is already set in auth store
      console.error('Password change failed:', err);
    }
  };

  // Show warning if user must change password
  const showForceChangeWarning = mustChangePassword;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LockResetIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                Change Password
              </Typography>
              {user && (
                <Typography variant="body2" color="text.secondary">
                  Logged in as: <strong>{user.username}</strong>
                </Typography>
              )}
            </Box>

            {/* Force Change Warning */}
            {showForceChangeWarning && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                You must change your password before continuing.
              </Alert>
            )}

            {/* Success Message */}
            {successMessage && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {successMessage}
              </Alert>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
                {error}
              </Alert>
            )}

            {/* Change Password Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Current Password Field */}
              <Controller
                name="old_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={showOldPassword ? 'text' : 'password'}
                    label="Current Password"
                    margin="normal"
                    autoComplete="current-password"
                    autoFocus
                    disabled={isLoading}
                    error={!!errors.old_password}
                    helperText={errors.old_password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowOldPassword(!showOldPassword)}
                            edge="end"
                          >
                            {showOldPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* New Password Field */}
              <Controller
                name="new_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={showNewPassword ? 'text' : 'password'}
                    label="New Password"
                    margin="normal"
                    autoComplete="new-password"
                    disabled={isLoading}
                    error={!!errors.new_password}
                    helperText={
                      errors.new_password?.message ||
                      'Min 8 characters, 3 of: lowercase, uppercase, digit, special char'
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              {/* Confirm New Password Field */}
              <Controller
                name="confirm_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm New Password"
                    margin="normal"
                    autoComplete="new-password"
                    disabled={isLoading}
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />
                )}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <LockResetIcon />}
                sx={{ mb: 2 }}
              >
                {isLoading ? 'Changing Password...' : 'Change Password'}
              </Button>

              {/* Cancel Button (only if not forced) */}
              {!showForceChangeWarning && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  disabled={isLoading}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 3 }}
        >
          NetGraph v1.0 - Network Configuration Visualization Platform
        </Typography>
      </Container>
    </Box>
  );
}
